import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Users, Lock, Loader2, CheckCircle2, AlertTriangle } from "lucide-react";
import { API } from "../App";
import { toast } from "sonner";

const TeamSetup = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [inviteData, setInviteData] = useState(null);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [name, setName] = useState("");

  useEffect(() => {
    verifyInvite();
  }, [token]);

  const verifyInvite = async () => {
    try {
      const res = await axios.post(`${API}/team/team-members/verify-invite`, { token });
      setInviteData(res.data);
      setName(res.data.member_name || "");
    } catch (err) {
      setError(err.response?.data?.detail || "Invalid or expired invite link");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    if (password !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/team/team-members/accept-invite`, {
        token,
        password,
        name: name.trim() || undefined,
      });
      if (res.data.token) {
        localStorage.setItem("token", res.data.token);
        localStorage.setItem("auth_kind", "owner"); // marker for silent-refresh interceptor
        toast.success("Account created! Redirecting to dashboard...");
        setTimeout(() => navigate("/dashboard"), 1000);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create account");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="max-w-md w-full border-border bg-card">
          <CardContent className="p-8 text-center space-y-4">
            <AlertTriangle className="w-12 h-12 text-amber-400 mx-auto" />
            <h2 className="text-xl font-bold text-foreground">Invalid Invite</h2>
            <p className="text-muted-foreground">{error}</p>
            <Button onClick={() => navigate("/login")} variant="outline" className="border-border text-foreground" data-testid="go-to-login-btn">
              Go to Login
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <Card className="max-w-md w-full border-border bg-card" data-testid="team-setup-card">
        <CardHeader className="text-center pb-2">
          <div className="w-14 h-14 rounded-xl bg-blue-500/15 flex items-center justify-center mx-auto mb-3">
            <Users className="w-7 h-7 text-blue-400" />
          </div>
          <CardTitle className="text-xl text-foreground font-['Manrope']">Join {inviteData?.business_name || "the team"}</CardTitle>
          <CardDescription className="text-muted-foreground">
            {inviteData?.owner_name ? `${inviteData.owner_name} has invited you` : "You've been invited"} as a{" "}
            <Badge className="text-[10px] px-1.5 py-0 bg-blue-500/15 text-blue-400 border border-blue-500/30 ml-1">
              {inviteData?.member_role || "member"}
            </Badge>
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label className="text-muted-foreground text-sm">Your Name</Label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Full Name"
                className="bg-background border-border text-foreground mt-1"
                data-testid="team-setup-name"
              />
            </div>
            <div>
              <Label className="text-muted-foreground text-sm">Email</Label>
              <Input
                value={inviteData?.member_email || ""}
                disabled
                className="bg-muted/50 border-border text-muted-foreground mt-1"
                data-testid="team-setup-email"
              />
            </div>
            <div>
              <Label className="text-muted-foreground text-sm">Password</Label>
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Min 8 characters"
                required
                className="bg-background border-border text-foreground mt-1"
                data-testid="team-setup-password"
              />
            </div>
            <div>
              <Label className="text-muted-foreground text-sm">Confirm Password</Label>
              <Input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm password"
                required
                className="bg-background border-border text-foreground mt-1"
                data-testid="team-setup-confirm-password"
              />
            </div>
            <Button
              type="submit"
              disabled={submitting || !password || !confirmPassword}
              className="w-full bg-[#0046BE] hover:bg-[#003da6] text-white h-11"
              data-testid="team-setup-submit"
            >
              {submitting ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Creating Account...</>
              ) : (
                <><CheckCircle2 className="w-4 h-4 mr-2" /> Create Account & Join</>
              )}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default TeamSetup;
